import { useMemo } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useLoad } from '../kit';

// Small helpers shared by Overview ("My day"), the Log time sheet and its participant picker.
//
// Browser storage only ever holds conveniences: the participants you logged time for last (keyed by
// the signed-in user) and the activity used last (`sc:last-activity-type`, shared with Referrals).
// Every read and write is wrapped (private windows, blocked storage); nothing breaks when empty.

const RECENT = 'sc:recent-participants';
// Shared with the Referrals screen (follow-up time): one remembered activity for both.
const ACTIVITY = 'sc:last-activity-type';
const keyFor = (base, userId) => (userId ? `${base}:${userId}` : base);

export function readRecent(userId) {
  try {
    const v = JSON.parse(window.localStorage.getItem(keyFor(RECENT, userId)) || '[]');
    return Array.isArray(v) ? v.filter((x) => typeof x === 'string').slice(0, 12) : [];
  } catch { return []; }
}
export function rememberParticipant(userId, id) {
  if (!id) return;
  try {
    const next = [String(id), ...readRecent(userId).filter((x) => x !== String(id))].slice(0, 12);
    window.localStorage.setItem(keyFor(RECENT, userId), JSON.stringify(next));
  } catch { /* storage unavailable: the picker just has no "recent" group */ }
}
export function readLastActivity() {
  try { return window.localStorage.getItem(ACTIVITY) || ''; } catch { return ''; }
}
export function rememberActivity(id) {
  if (!id) return;
  try { window.localStorage.setItem(ACTIVITY, String(id)); } catch { /* storage unavailable */ }
}

export const nameOf = (row) => (row ? `${row.first_name || ''} ${row.last_name || ''}`.trim() || row.name || 'Participant' : '');
// A participant you can still log time for: the engagement is active and not closed.
export const isLive = (row) => !!row && row.is_active !== false && row.status !== 'closed';

// "Mine" = the engagement's support coordinator is the signed-in person. The caseload list sends the
// coordinator's name; when it also sends coordinator_id that is used instead.
export function isMine(row, me) {
  if (!row || !me) return false;
  if (row.coordinator_id && me.userId) return String(row.coordinator_id) === String(me.userId);
  const theirs = `${row.coordinator_first || ''} ${row.coordinator_last || ''}`.trim().toLowerCase();
  const mine = `${me.firstName || ''} ${me.lastName || ''}`.trim().toLowerCase();
  return !!theirs && !!me.firstName && !!me.lastName && theirs === mine;
}

// Whole days from one ISO date to another (dates only, no time-zone drift).
const utc = (iso) => {
  const [y, m, d] = String(iso || '').slice(0, 10).split('-').map(Number);
  return y && m && d ? Date.UTC(y, m - 1, d) : null;
};
export function daysBetween(fromIso, toIso) {
  const a = utc(fromIso); const b = utc(toIso);
  return a == null || b == null ? null : Math.round((b - a) / 86400000);
}
export const agoText = (days) => (days == null ? null : days <= 0 ? 'today' : days === 1 ? 'yesterday' : `${days} days ago`);
export const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// Today in the browser's own clock, for the quick-time chips.
const pad = (n) => String(n).padStart(2, '0');
export const localYmd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const localHm = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;

// Who is signed in, with their SC permissions: { userId, firstName, lastName, perms, loading, error, reload }.
export function useScMe({ skip = false } = {}) {
  const auth = useAuth();
  const user = (auth && auth.user) || null;
  const me = useLoad('/sc/me', null, { skip });
  // An impersonated session carries the company's name, not a person's.
  const person = user && !user.impersonating ? user : null;
  const firstName = person ? (person.firstName || person.first_name || '') : '';
  const lastName = person ? (person.lastName || person.last_name || '') : '';
  const { data, loading, error, reload } = me;
  return useMemo(() => ({
    userId: data ? data.userId : null, firstName, lastName,
    perms: (data && data.permissions) || NONE,
    settings: (data && data.settings) || NO_SETTINGS,
    loading, error, reload, loaded: !!data,
  }), [data, firstName, lastName, loading, error, reload]);
}
const NONE = [];
const NO_SETTINGS = {};
